import { recaptcha } from "@turo/libraries/lib/recaptcha/recaptcha";
import type {
  ApiErrorResponse,
  DriverAccountResponse,
} from "@turo/types-dunlop";
import type { CountryCode } from "libphonenumber-js/mobile";

import {
  GENERATE_OTP_ENDPOINT,
  LOGIN_WITH_OTP_ENDPOINT,
} from "../constants/api";
import type { EmailOtpMethod, SMSOtpMethod } from "../constants/otp";
import SocureService from "../helpers/socure";
import { type CLIENT_REQUEST_ERROR, post, postJSON } from "./iframeHelpers";

/** Dunlop currently defines these nullish types as stronger than they are, at least for the security_challenge_required response */
interface NullishApiErrorResponse extends Omit<ApiErrorResponse, "ApiError"> {
  additionalDescription?: string | null;
  code: string;
  data?: string | null;
  field?: string | null;
  message?: string | null;
}

interface GenerateEmailOTPOptions {
  email: string;
  method: EmailOtpMethod;
}

interface GeneratePhoneOTPOptions {
  countryCode: CountryCode;
  method: SMSOtpMethod;
  phoneNumber: string;
}

export type ClientRequestError = { message: string };

export type ClientError =
  | {
      error: typeof CLIENT_REQUEST_ERROR;
      errors: readonly [ClientRequestError];
    }
  | NullishApiErrorResponse;

interface GenerateOTPResponse {
  token: string;
}

interface GenerateOTPOptions {
  body: GenerateEmailOTPOptions | GeneratePhoneOTPOptions;
  recaptchaToken: string;
}

export const generateOTP = ({
  body,
  recaptchaToken,
}: GenerateOTPOptions): Promise<GenerateOTPResponse | ClientError> =>
  postJSON<GenerateOTPResponse, ClientRequestError>({
    body,
    endpoint: GENERATE_OTP_ENDPOINT,
    headers: { [recaptcha.HEADER_NAME]: recaptchaToken },
  });

interface LoginWithOTPOptions {
  passcode: string;
  recaptchaToken: string;
  token: string;
}

async function fetchSessionId() {
  try {
    return await SocureService.getSocureSessionId();
  } catch (error) {
    console.error("Error loading Socure session ID:", error);
    return "";
  }
}

export const loginWithOTP = async ({
  passcode,
  recaptchaToken,
  token,
}: LoginWithOTPOptions): Promise<DriverAccountResponse | ClientError> => {
  const sessionId = await fetchSessionId();
  return post<DriverAccountResponse, ClientRequestError>(
    LOGIN_WITH_OTP_ENDPOINT,
    { passcode, token },
    {
      "X-Socure-Device-Session-Id": sessionId,
      [recaptcha.HEADER_NAME]: recaptchaToken,
    },
  );
};
